import {FC, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {getCurrentSchedules} from "../../../../../../../redux/selectors/patientsSelector";
import {IMedication} from "../../../../../../../utils/interfaces";
import {Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {Add} from "../../../../../../../assets/icons/Add";
import {Edit} from "../../../../../../../assets/icons/Edit";
import {Delete} from "../../../../../../../assets/icons/Delete";
import classes from './MedicationsTable.module.scss';
import {differenceInCalendarDays, format, isAfter, isValid, parse} from "date-fns";
import {PeriodToggle, PeriodToggleRef} from "../../../ClickingOverview/components/PeriodToggle/PeriodToggle";
import {ClearIcon, DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {NoDataPage} from "../../../../../../common/NoDataPage/NoDataPage";

type MedicationsTableProps = {
  onEditMedication: (medication: IMedication) => void;
  onRemoveMedication: (medication: IMedication) => void;
  onAddMedication: () => void;
  setDates: React.Dispatch<React.SetStateAction<{start: Date; end: Date} | null>>;
}

export const MedicationsTable: FC<MedicationsTableProps> = ({onAddMedication, onEditMedication, onRemoveMedication, setDates}) => {
  const patientMedications: Record<string, IMedication[]> = useSelector(getCurrentSchedules)
  const periodToggleRef = useRef<PeriodToggleRef | null>(null);

  const [selectedDate, setSelectedDate] = useState<any>(null);

  const onChangeDate = (newDate: Date) => {
    if (isValid(newDate) && isAfter(newDate, parse('01-01-2020', 'dd-MM-yyyy', new Date()))) {
      if (periodToggleRef.current && newDate) {
        periodToggleRef.current.setCentralDate(newDate);
      }
  
      setSelectedDate(newDate);
    }
  }

  const getEmptyText = () => {
    if (selectedDate) {
      return 'No medications are scheduled for this date';
    }

    return 'No medications are scheduled for this period';
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
      <div style={{display: 'flex', gap: 16}}>
        <PeriodToggle ref={periodToggleRef} setDates={setDates} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className={classes.dateInput}
            format="dd.MM.yyyy"
            minDate={parse('01-01-2020', 'dd-MM-yyyy', new Date())}
            slots={{
              clearIcon: ClearIcon
            }}
            onChange={newValue => onChangeDate(newValue)}
            slotProps={{
              textField: {
                placeholder: "Filter by specific date",
                variant: "outlined",
                size: 'small',
              },
              field: {
                clearable: true,
                onClear: () => setSelectedDate(null),
              },
            }}
            value={selectedDate}
          />
        </LocalizationProvider>
        <div style={{flex: 1, display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            onClick={onAddMedication}
            startIcon={<Add />}
            variant="contained"
            sx={{alignSelf: 'center'}}
          >
            Add medication
          </Button>
        </div>
      </div>
      {Object.values(patientMedications).flat().length ? (
        <TableContainer sx={{height: 430}} component={Paper}>
          <Table stickyHeader sx={{minWidth: 650}} aria-label="simple table">
            <TableHead>
              <TableRow classes={{
                root: classes.tableHeader
              }}>
                <TableCell>Date</TableCell>
                <TableCell>Medication Name</TableCell>
                <TableCell align="left">Dosage</TableCell>
                <TableCell align="center">Times</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(patientMedications).map(([date, medications]) => {
                const medicationsToShow = selectedDate && format(selectedDate, 'yyyy-MM-dd') === date ? medications : [];

                return (selectedDate ? medicationsToShow : medications).map((medication) => {
                  const startDate = parse(medication.startDate, 'yyyy-MM-dd', new Date());
                  const endDate = parse(medication.endDate, 'yyyy-MM-dd', new Date());
                  const daysDifference = differenceInCalendarDays(endDate, startDate) + 1;

                  return (
                    <TableRow
                      key={medication.id}
                      sx={{'&:last-child td, &:last-child th': {border: 0}}}
                    >
                      <TableCell component="th" scope="row">
                        {format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {medication.name}
                      </TableCell>
                      <TableCell align="left">{medication.dosageValue} {medication.dosageUnit}</TableCell>
                      <TableCell align="left">
                        <div style={{display: 'flex', flexDirection: 'row', gap: 8}}>
                          {medication.times.map((time, index) => {
                            const splittedTime = time.split(':');
                            const formattedTime = `${splittedTime[0]}:${splittedTime[1]}`;

                            return (
                              <Chip size="small" key={index} label={formattedTime} variant="outlined" />
                            )
                          })}
                        </div>
                      </TableCell>
                      <TableCell align="right">{daysDifference} day{daysDifference > 1 ? 's' : ''}</TableCell>
                      <TableCell align="right">
                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
                          <Button sx={{marginRight: 2}} variant="contained" onClick={() => onEditMedication(medication)}>
                            <Edit className={classes.editIcon} />
                          </Button>
                          <Button variant="outlined" onClick={() => onRemoveMedication(medication)}>
                            <Delete />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>) : (
        <NoDataPage
          message={getEmptyText()}
          description=""
        />
      )}
    </div>
  )
}